import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { generateStrongPassword } from "@/lib/utils";
import { Eye, EyeClosed } from "lucide-react";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { UserFormValues } from "./form";
import { accessLevelService, AccessLevel } from "@/services/api/accessLevel";

interface UserDataTabProps {
  form: UseFormReturn<UserFormValues>;
}

const UserDataTab = ({ form }: UserDataTabProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);

  useState(() => {
    accessLevelService.index({ page: 1, per_page: 100 }).then((res) => {
      setAccessLevels(res.data);
    });
  });

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleGeneratePassword = () => {
    form.setValue("user_data.password", generateStrongPassword(), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setShowPassword(true);
  };

  return (
    <TabsContent value="user_data" className="space-y-6 mt-4">
      <FormField
        control={form.control}
        name="user_data.status"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Status</FormLabel>
            <Select
              onValueChange={(value) => field.onChange(Number(value))}
              value={String(field.value)}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="1">Ativo</SelectItem>
                <SelectItem value="0">Inativo</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="user_data.access_level_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Perfil de acesso</FormLabel>
            <Select
              onValueChange={(value) =>
                field.onChange(value === "none" ? null : Number(value))
              }
              value={field.value ? String(field.value) : "none"}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="none">
                  Nenhum (Permissões individuais)
                </SelectItem>
                {accessLevels.map((level) => (
                  <SelectItem key={level.id} value={String(level.id)}>
                    {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="user_data.name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nome</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="user_data.email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>E-mail</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ""}
                onChange={(e) => {
                  const value =
                    e.target.value.trim() === "" ? null : e.target.value;
                  field.onChange(value);
                }}
              />
            </FormControl>
            <FormDescription>
              É para onde vão as notificações dos veículos habilitados para esse
              usuário.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <p className="text-xs text-muted-foreground">
        Usuário e senha são opcionais: preencha para dar acesso à plataforma, ou
        deixe em branco para que esta pessoa seja apenas destinatária de
        notificações.
      </p>

      <FormField
        control={form.control}
        name="user_data.user"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Usuário</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ""} autoComplete="off" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="user_data.password"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel>Senha</FormLabel>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGeneratePassword}
              >
                Gerar senha forte
              </Button>
            </div>
            <FormControl>
              <div className="relative">
                <Input
                  {...field}
                  value={field.value || ""}
                  onChange={(e) => {
                    const value =
                      e.target.value.trim() === "" ? null : e.target.value;
                    field.onChange(value);

                    if (!value) {
                      form.clearErrors("user_data.password");
                      form.resetField("user_data.password", {
                        defaultValue: undefined,
                        keepDirty: false,
                        keepTouched: false,
                        keepError: false,
                      });
                    }
                  }}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                >
                  {showPassword ? <EyeClosed /> : <Eye />}
                </button>
              </div>
            </FormControl>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Mínimo de 8 caracteres, com letra maiúscula, minúscula, número e
              símbolo.
            </p>
            <FormMessage />
          </FormItem>
        )}
      />
    </TabsContent>
  );
};

export default UserDataTab;
